/**
 * CardShell — host for all 11 v2 card types in HITL and terminal states.
 *
 * Owns:
 *   - Header (type chip + status pill)
 *   - Body slot (via pickCardBody dispatch)
 *   - Action footer (Cancel / Amend? / Confirm)
 *   - Past-state opacity treatment
 *   - Inline-edit dispatch wrapper: bodies call onEditField(field_updates)
 *     for per-control commits; the shell wraps into edit_field ResolveActions.
 *
 * Does NOT own:
 *   - Edit-mode state. Per-control inline edits dispatch immediately;
 *     there is no card-level Edit/Save/Discard mode (locked 2026-05-13).
 *   - Network IO. Parent owns POST /v2/cards/resolve and re-renders
 *     the shell with new content when a new card emission arrives.
 *   - Plan state. Parent owns Turn.resolved and computed "past-gate"
 *     flag and passes both via props.
 *
 * Spec: v2-design/CARD_SHELL_v0_spec.md §4.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { CardType, PlanStatus, ResolveAction } from '../../types';

import { summarizeForCollapse } from './collapsedSummary';
import {
  cardTypeChipLabel,
  cardTypeSubtitle,
  cardUsesAmend,
  expectedStatusForCard,
  statusPillSpec,
} from './labels';
import { pickCardBody } from './pickCardBody';
import { StatusPill } from './StatusPill';

export interface CardShellProps {
  cardType: CardType;
  content: Record<string, unknown>;
  planId: string | null;
  /**
   * Plan status from the parent's turn-list view. Drives the status
   * pill label/tone and decides whether the action footer renders.
   * null for non-plan cards (scope_proposal, ambiguity, explain).
   */
  planStatus: PlanStatus | null;
  /**
   * Local UI-side resolved state set when this shell's action fires
   * (confirm/cancel/edit/amend). Drives past-state visuals.
   */
  resolved?: 'confirmed' | 'cancelled' | 'edited' | 'amended';
  /**
   * Computed signal that this card has been superseded by a newer
   * card emission for the same plan_id. Treated as past-state.
   */
  isPastGate?: boolean;
  /**
   * When true, a past-state card collapses to its header + summary line
   * with a chevron to re-expand. When false / omitted, past-state cards
   * stay fully visible (used for terminal result / explain cards).
   * Has no effect on active cards.
   */
  forceCollapsed?: boolean;
  /** True while any /v2 request is in flight. Disables the footer + edits. */
  busy: boolean;
  /**
   * Resolve callback. Shell builds the ResolveAction discriminated
   * union from button clicks + body edit dispatches and calls this.
   */
  onResolve: (action: ResolveAction) => void | Promise<void>;
  /**
   * Optional extra actions rendered left-aligned in their own footer
   * row. Unlike the Confirm/Cancel footer, these render on terminal
   * and past-state cards too (suppressed only when collapsed) — used
   * for non-resolve affordances like "Save to prompt library" on a
   * completed CV result card.
   */
  extraActions?: React.ReactNode;
}

export const CardShell: React.FC<CardShellProps> = ({
  cardType,
  content,
  planId,
  planStatus,
  resolved,
  isPastGate,
  forceCollapsed,
  busy,
  onResolve,
  extraActions,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const isTerminal = useMemo(
    () => cardType === 'result_card' || cardType === 'explain_answer_card',
    [cardType]
  );
  const isPastState = !!resolved || !!isPastGate;
  const pillSpec = useMemo(
    () => statusPillSpec(cardType, planStatus, resolved),
    [cardType, planStatus, resolved]
  );
  const chipLabel = useMemo(
    () => cardTypeChipLabel(cardType, content),
    [cardType, content]
  );
  const usesAmend = useMemo(() => cardUsesAmend(cardType), [cardType]);
  const showFooter =
    !isTerminal &&
    !isPastState &&
    (planId !== null || cardType === 'scope_proposal_card');

  const submit = useCallback(
    async (action: ResolveAction) => {
      setSubmitting(true);
      setInlineError(null);
      try {
        await onResolve(action);
      } catch (e) {
        setInlineError(
          e instanceof Error ? e.message : 'Failed to submit. Try again.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [onResolve]
  );

  const handleConfirm = useCallback(
    () => submit({ action: 'confirm' }),
    [submit]
  );
  const handleCancel = useCallback(
    () => submit({ action: 'cancel' }),
    [submit]
  );
  const handleAmend = useCallback(() => submit({ action: 'amend' }), [submit]);
  const handleEditField = useCallback(
    (field_updates: Record<string, unknown>) => {
      if (!field_updates || Object.keys(field_updates).length === 0) return;
      submit({ action: 'edit_field', field_updates });
    },
    [submit]
  );

  const footerDisabled = busy || submitting;
  const editsDisabled = footerDisabled || isPastState;

  // Cards collapse only when past-state AND the parent flagged them as
  // "preserved but collapsed" (sub-agent param cards confirmed/cancelled).
  // Terminal result/explain cards stay fully visible past-state because
  // forceCollapsed isn't set. Re-syncs when the card transitions active
  // → past so a freshly resolved card collapses without a click.
  const collapsible = isPastState && !!forceCollapsed;
  const [expanded, setExpanded] = useState<boolean>(!collapsible);
  useEffect(() => {
    setExpanded(!collapsible);
  }, [collapsible]);

  const collapsedSummary = useMemo(
    () => summarizeForCollapse(cardType, content),
    [cardType, content]
  );
  const showCollapsedSummary = collapsible && !expanded;
  const headerClickable = collapsible;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: 'grey.300',
        opacity: isPastState ? 0.6 : 1,
        transition: 'opacity 150ms ease',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header — clickable when past-state to toggle expanded/collapsed. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={headerClickable ? () => setExpanded((x) => !x) : undefined}
          sx={{
            mb: showCollapsedSummary ? 0 : 1.5,
            cursor: headerClickable ? 'pointer' : 'default',
            userSelect: 'none',
          }}
        >
          <Chip
            label={chipLabel}
            size="small"
            sx={{ fontWeight: 600, fontSize: 13 }}
          />
          {cardTypeSubtitle(cardType) && (
            <Chip
              label={cardTypeSubtitle(cardType)}
              size="small"
              variant="outlined"
              sx={{ fontSize: 12, fontWeight: 400, height: 24 }}
            />
          )}
          {showCollapsedSummary && collapsedSummary && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                ml: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {collapsedSummary}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <StatusPill label={pillSpec.label} tone={pillSpec.tone} />
          {headerClickable && (
            <IconButton
              size="small"
              aria-label={expanded ? 'Collapse card' : 'Expand card'}
              sx={{
                p: 0.25,
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 150ms ease',
              }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((x) => !x);
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* Body — suppressed when past-state and collapsed. */}
        {!showCollapsedSummary &&
          pickCardBody({
            cardType,
            content,
            onEditField: handleEditField,
            editsDisabled,
          })}

        {/* Inline submit error */}
        {inlineError && (
          <Alert
            severity="error"
            sx={{ mt: 1.5, py: 0.5 }}
            onClose={() => setInlineError(null)}
          >
            {inlineError}
          </Alert>
        )}

        {/* Action footer */}
        {showFooter && (
          <>
            <Divider sx={{ my: 1.75 }} />
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
            >
              {submitting && <CircularProgress size={14} />}
              <Button
                size="small"
                onClick={handleCancel}
                disabled={footerDisabled}
                color="inherit"
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              {usesAmend && (
                <Tooltip
                  title="Go back to input selection to make changes."
                  enterDelay={500}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleAmend}
                    disabled={footerDisabled}
                    color="inherit"
                    sx={{ textTransform: 'none' }}
                  >
                    Amend
                  </Button>
                </Tooltip>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={handleConfirm}
                disabled={footerDisabled}
                sx={{ textTransform: 'none' }}
              >
                Confirm
              </Button>
            </Stack>
          </>
        )}

        {/* Extra actions — independent of showFooter so they survive on
            terminal/past-state cards; hidden only when collapsed. */}
        {extraActions && !showCollapsedSummary && (
          <>
            <Divider sx={{ my: 1.75 }} />
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-start"
              alignItems="center"
            >
              {extraActions}
            </Stack>
          </>
        )}

        {/* Past-state caption — only when expanded (collapsed view already
            communicates past-state via the status pill + chevron). */}
        {isPastState && resolved && expanded && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {pastStateCaption(resolved)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

function pastStateCaption(
  resolved: 'confirmed' | 'cancelled' | 'edited' | 'amended'
): string {
  switch (resolved) {
    case 'confirmed':
      return 'Confirmed.';
    case 'cancelled':
      return 'Cancelled.';
    case 'edited':
      return 'Edited — see updated card below.';
    case 'amended':
      return 'Amended — back to input selection.';
  }
}

// Re-export so consumers don't need to import from labels.
export { expectedStatusForCard };
